import React, {
  useEffect,
  useRef,
  ElementType,
  ReactNode,
  ComponentPropsWithoutRef,
  ReactElement,
  ComponentProps,
} from "react";
import { useLocation } from "react-router";

export type HashNavScrollTargetProps<T extends ElementType = "div"> = Omit<ComponentPropsWithoutRef<T>, "id"> & {
  /**
   * The HTML tag or React component to use for the nav target element.
   * @default 'div'
   */
  as?: T;

  /** The content to be rendered inside the nav target element. */
  children?: ReactNode;

  /**
   * The unique ID for the nav target element, used for matching against the URL hash. This 'id' will also be applied to
   * the rendered nav target element.
   */
  id: string;
};

/**
 * A component that creates a nav target element (e.g. a 'div' or other specified tag) that automatically scrolls into
 * view when its `elementId` prop matches the URL hash. This component is intended to be a target for hash-based
 * navigation.
 *
 * @template T - The HTML tag type for the nav target element. Defaults to 'div'.
 * @param props - The props to apply to the nav target element.
 * @returns {ReactElement | null} The rendered nav target element with its children.
 */
export const HashNavScrollTarget = <T extends ElementType = "div">(
  props: HashNavScrollTargetProps<T>,
): ReactElement | null => {
  const { as: NavTarget = "div" as T, children, id, ...remainingProps } = props;

  const location = useLocation();
  const navTargetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Remove the '#' from the hash
    const hash = location.hash.slice(1);

    // Do nothing if the hash does not match the elementId or if the navTargetRef is not set
    if (hash !== id || !navTargetRef.current) return;
    const targetElem = navTargetRef.current;

    // Scroll to the target element. Wait a moment to ensure any layout changes are complete before scrolling.
    // Specifically, when scrolling to a table row, this gives time to allow the sticky header hight to be calculated
    const startScrollTimeout = setTimeout(() => {
      targetElem.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 1);

    // Add a class to highlight the target element for a short duration
    // Wait a short time before adding the class to allow the scroll to complete
    const addClass = () => {
      targetElem.classList.add("hash-nav-scroll-target-highlight");
    };
    const removeClass = () => {
      targetElem.classList.remove("hash-nav-scroll-target-highlight");
    };
    const addClassTimeout = setTimeout(addClass, 300);
    const removeClassTimeout = setTimeout(removeClass, 5300);

    // Cleanup function to clear timeouts and remove the highlight class when the component unmounts or the hash changes
    return () => {
      clearTimeout(startScrollTimeout);
      clearTimeout(addClassTimeout);
      clearTimeout(removeClassTimeout);
      removeClass();
    };
  }, [location.hash, id]);

  // Render the NavTarget (e.g., 'div', 'section', or a custom component type)
  // Apply the internally managed ref and the 'id' props; spread all other props
  const navTargetProps = { ref: navTargetRef, id, ...remainingProps } as ComponentProps<T>;
  return React.createElement<ComponentProps<T>>(NavTarget, navTargetProps, children);
};
